import type { DisciplineUpdatePayload } from '../model/types'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface DisciplineUpdateApiOptions {
  disciplineId: string | number
  payload: DisciplineUpdatePayload
  onStart?: () => void
  onSuccess?: () => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/** `PATCH /admin-api/disciplines/{discipline}` — partial: only the keys in `payload` change. */
export const useDisciplineUpdateApi = async (options: DisciplineUpdateApiOptions): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.disciplines.api.update', { discipline_id: options.disciplineId })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return false
    }

    await $axios.patch<ApiResponse>(url, options.payload)

    toast.success({ title: 'Successfully updated!', description: '' })
    options.onSuccess?.()

    return true
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error({ title: 'Error!', description: msg })
    options.onError?.(msg)

    return false
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
